import { useRef, useState } from 'react'
import { AppBar, Box, Button, IconButton, Toolbar, Typography, useScrollTrigger } from '@mui/material'
import { AnimatePresence, motion, useScroll } from 'framer-motion'
import { Link as RouterLink } from 'react-router-dom'
import { ArrowUpRight, ChevronDown, Menu as MenuIcon } from 'lucide-react'
import { colors, fonts, layout, zIndex } from '@/theme'
import { categories, industries, resourceLinks } from '@/content/site'
import { Container } from '@/components/ui/Container'
import { Logo } from '@/components/ui/Logo'
import { MobileDrawer } from './MobileDrawer'

type MenuKey = 'products' | 'industries' | 'resources'

interface MegaLink {
  label: string
  to: string
  caption?: string
}
interface MegaMenu {
  eyebrow: string
  title: string
  blurb: string
  rootTo?: string
  rootLabel?: string
  links: MegaLink[]
}

// Full-width mega-menus that drop down from the bar on hover.
const megaMenus: Record<MenuKey, MegaMenu> = {
  products: {
    eyebrow: 'Catalogue',
    title: 'Product Range',
    blurb: 'Welding gloves, driver gloves, caps and leather garments — engineered for the arc.',
    rootTo: '/products',
    rootLabel: 'All Products',
    links: categories.map((c) => ({ label: c.name, to: `/products/${c.slug}`, caption: c.comingSoon ? 'Coming Soon' : undefined })),
  },
  industries: {
    eyebrow: 'Sectors',
    title: 'Industries We Serve',
    blurb: 'Protection tailored to the hazards of each sector we supply.',
    rootTo: '/industries',
    rootLabel: 'All Industries',
    links: industries.map((i) => ({ label: i.name, to: `/industries/${i.slug}` })),
  },
  resources: {
    eyebrow: 'Company',
    title: 'Resources',
    blurb: 'Guides, standards and everything you need to specify with confidence.',
    links: resourceLinks.map((l) => ({ label: l.label, to: l.to })),
  },
}

/** Primary navigation. Crystal (frosted-glass) bar with a full-width hover mega-menu. */
export function Header() {
  const solid = useScrollTrigger({ disableHysteresis: true, threshold: 24 })
  const { scrollYProgress } = useScroll()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [menu, setMenu] = useState<MenuKey | null>(null)

  // Small close delay so moving the cursor from a nav item into the panel doesn't snap it shut.
  const closeTimer = useRef<number | undefined>(undefined)
  const cancelClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => setMenu(null), 130)
  }
  const openNow = (m: MenuKey) => () => {
    cancelClose()
    setMenu(m)
  }
  const closeNow = () => {
    cancelClose()
    setMenu(null)
  }

  // Frosted glass whenever scrolled or a mega-menu is open (so it never floats transparent).
  const glassy = solid || menu !== null

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        onMouseLeave={scheduleClose}
        sx={{
          zIndex: zIndex.header,
          // Menu open → solid (continuous with the panel); scrolled → crystal glass; over hero → transparent.
          backgroundColor: menu ? 'rgba(11,11,11,0.98)' : solid ? 'rgba(13,13,13,0.55)' : 'transparent',
          backdropFilter: glassy ? 'blur(18px) saturate(160%)' : 'none',
          WebkitBackdropFilter: glassy ? 'blur(18px) saturate(160%)' : 'none',
          borderBottom: `1px solid ${glassy && !menu ? colors.border : 'transparent'}`,
          transition: 'background-color 300ms ease, border-color 300ms ease',
        }}
      >
        <Container>
          <Toolbar disableGutters sx={{ minHeight: { xs: 60, md: layout.headerHeight }, gap: 2 }}>
            {/* Smaller logo on mobile so the bar isn't oversized */}
            <Box sx={{ display: 'inline-flex', '& img': { height: { xs: 38, md: 56 } } }}>
              <Logo />
            </Box>
            <Box sx={{ flex: 1 }} />

            {/* Desktop nav */}
            <Box component="nav" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              <Button variant="text" component={RouterLink} to="/" onMouseEnter={closeNow}>
                Home
              </Button>
              {(['products', 'industries', 'resources'] as MenuKey[]).map((key) => (
                <Button
                  key={key}
                  variant="text"
                  onMouseEnter={openNow(key)}
                  onClick={openNow(key)}
                  endIcon={
                    <ChevronDown
                      size={16}
                      style={{ transform: menu === key ? 'rotate(180deg)' : 'none', transition: 'transform 250ms ease' }}
                    />
                  }
                  sx={{ color: menu === key ? colors.gold : undefined }}
                >
                  {key[0].toUpperCase() + key.slice(1)}
                </Button>
              ))}
              <Button variant="text" component={RouterLink} to="/about" onMouseEnter={closeNow}>
                About
              </Button>
              <Button variant="text" component={RouterLink} to="/contact" onMouseEnter={closeNow}>
                Contact
              </Button>
              <Button variant="outlined" component={RouterLink} to="/contact" size="small" sx={{ ml: 2 }} onMouseEnter={closeNow}>
                Request Quote
              </Button>
            </Box>

            {/* Mobile trigger */}
            <IconButton
              aria-label="Open menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, color: colors.white }}
            >
              <MenuIcon size={26} />
            </IconButton>
          </Toolbar>
        </Container>

        {/* Scroll progress bar */}
        <Box
          component={motion.div}
          aria-hidden
          style={{ scaleX: scrollYProgress }}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            transformOrigin: '0% 50%',
            backgroundColor: colors.gold,
            opacity: solid ? 1 : 0,
            transition: 'opacity 300ms ease',
          }}
        />

        {/* Full-width mega-menu panel — anchored flush to the bar's bottom edge (no gap) */}
        <AnimatePresence>
          {menu && (
          <Box
            key={menu}
            component={motion.div}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              display: { xs: 'none', md: 'block' },
              // Near-opaque so the page content behind it doesn't bleed through the glass.
              backgroundColor: 'rgba(11,11,11,0.98)',
              backdropFilter: 'blur(22px) saturate(140%)',
              WebkitBackdropFilter: 'blur(22px) saturate(140%)',
              borderBottom: `1px solid ${colors.border}`,
              boxShadow: '0 28px 56px -20px rgba(0,0,0,0.9)',
            }}
          >
            <Container sx={{ display: 'flex', gap: 5, pt: { md: 2.5 }, pb: { md: 3 } }}>
              {/* Intro column */}
              <Box sx={{ minWidth: 240, maxWidth: 280, pr: 5, borderRight: `1px solid ${colors.border}` }}>
                <Typography sx={{ fontFamily: fonts.label, fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.gold }}>
                  {megaMenus[menu].eyebrow}
                </Typography>
                <Typography variant="h4" sx={{ mt: 1.25, mb: 1.5 }}>
                  {megaMenus[menu].title}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.grey, mb: 2.5 }}>
                  {megaMenus[menu].blurb}
                </Typography>
                {megaMenus[menu].rootTo && (
                  <Button variant="outlined" size="small" component={RouterLink} to={megaMenus[menu].rootTo!} onClick={closeNow}>
                    {megaMenus[menu].rootLabel}
                  </Button>
                )}
              </Box>

              {/* Links — vertical list */}
              <Box
                component="ul"
                sx={{
                  flex: '0 1 420px',
                  listStyle: 'none',
                  m: 0,
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                }}
              >
                {megaMenus[menu].links.map((link) => (
                  <Box component="li" key={link.to} sx={{ listStyle: 'none' }}>
                    <Box
                      component={RouterLink}
                      to={link.to}
                      onClick={closeNow}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        px: 2,
                        py: 1.25,
                        borderRadius: `${layout.radius.button}px`,
                        color: colors.white,
                        textDecoration: 'none',
                        transition: 'background-color 200ms ease, color 200ms ease',
                        '&:hover': { backgroundColor: 'rgba(212,175,55,0.10)', color: colors.gold },
                        '&:hover .mega-arrow': { opacity: 1, transform: 'translate(0, 0)' },
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontFamily: fonts.label, fontWeight: 600, fontSize: 15, lineHeight: 1.2 }}>
                          {link.label}
                        </Typography>
                        {link.caption && (
                          <Typography sx={{ fontSize: 12, color: colors.grey, mt: 0.25 }}>{link.caption}</Typography>
                        )}
                      </Box>
                      <ArrowUpRight
                        className="mega-arrow"
                        size={16}
                        style={{ flexShrink: 0, opacity: 0, transform: 'translate(-4px, 4px)', transition: 'opacity 200ms ease, transform 200ms ease' }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Container>
          </Box>
        )}
        </AnimatePresence>
      </AppBar>

      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} categories={categories} industries={industries} />
    </>
  )
}
